// Mock fixtures for the portal domain, matching the prototype sample data in
//   docs/design/zooyloresponsive/project/Service Portal.dc.html
//   docs/design/zooyloresponsive/project/Admin Portal.dc.html
//
// Internally consistent with the prototypes: org "Bright Steps Centre", 12 seats,
// 5 in use (7 free), 6 members (1 admin + 5 members, 1 pending invite), and the
// six linked children (Amira / Daniel / Sofia / Yusuf / Maya / Leo) with their
// owner + viewer relationships and sharing states.
//
// Guardrails: children are pseudonymous UUIDs carrying only an owner-set
// displayLabel; shared data is engagement-only; no clinical fields anywhere.

import type {
  Account,
  Assignment,
  Child,
  ChildLink,
  Consent,
  License,
  HomeNote,
  Membership,
  Organisation,
  Seat,
  SessionSummary,
  SkillStepState,
  SkillSummary,
  StepStage,
  SupportProfile,
} from "./types";
import { addDays, addWeeks, localDay, mondayOf } from "../progress/weeks";

// --- Organisation ------------------------------------------------------------

export const organisation: Organisation = {
  id: "org-bright-steps",
  name: "Bright Steps Centre",
  type: "centre",
  seatLimit: 12,
  createdAt: "2026-01-15T08:00:00.000Z",
};

// --- Accounts ----------------------------------------------------------------
// Org members (adults who support children) ...
export const accounts: Account[] = [
  { id: "acc-marcus", email: "marcus@brightsteps.org", orgId: organisation.id, createdAt: "2026-01-15T08:05:00.000Z" },
  { id: "acc-lena", email: "lena@brightsteps.org", orgId: organisation.id, createdAt: "2026-01-20T09:00:00.000Z" },
  { id: "acc-priya", email: "priya@brightsteps.org", orgId: organisation.id, createdAt: "2026-01-22T09:00:00.000Z" },
  { id: "acc-aisha", email: "aisha@brightsteps.org", orgId: organisation.id, createdAt: "2026-02-01T09:00:00.000Z" },
  { id: "acc-david", email: "david@brightsteps.org", orgId: organisation.id, createdAt: "2026-02-10T09:00:00.000Z" },
  { id: "acc-tom", email: "tom@brightsteps.org", orgId: organisation.id, createdAt: "2026-06-15T09:00:00.000Z" },
  // ... and the owners (parents / caregivers) who created each child. No org.
  { id: "acc-priya-s", email: "priya.s@example.com", createdAt: "2026-03-10T19:00:00.000Z" },
  { id: "acc-reys", email: "the.reys@example.com", createdAt: "2026-03-18T19:00:00.000Z" },
  { id: "acc-lena-r", email: "lena.r@example.com", createdAt: "2026-03-28T19:00:00.000Z" },
  { id: "acc-hana", email: "hana.a@example.com", createdAt: "2026-04-02T19:00:00.000Z" },
  { id: "acc-tom-t", email: "tom.t@example.com", createdAt: "2026-04-05T19:00:00.000Z" },
  { id: "acc-sam", email: "sam.p@example.com", createdAt: "2026-04-09T19:00:00.000Z" },
];

// --- Memberships -------------------------------------------------------------

export const memberships: Membership[] = [
  { accountId: "acc-marcus", orgId: organisation.id, role: "admin" },
  { accountId: "acc-lena", orgId: organisation.id, role: "member" },
  { accountId: "acc-priya", orgId: organisation.id, role: "member" },
  { accountId: "acc-aisha", orgId: organisation.id, role: "member" },
  { accountId: "acc-david", orgId: organisation.id, role: "member" },
  { accountId: "acc-tom", orgId: organisation.id, role: "member" },
];

// --- Children (pseudonymous UUID + owner-set displayLabel ONLY) ---------------

export const children: Child[] = [
  { id: "child-3f1a", ownerAccountId: "acc-priya-s", displayLabel: "Amira K." },
  { id: "child-9c2b", ownerAccountId: "acc-reys", displayLabel: "Daniel R." },
  { id: "child-7e44", ownerAccountId: "acc-lena-r", displayLabel: "Sofia M." },
  { id: "child-1d80", ownerAccountId: "acc-hana", displayLabel: "Yusuf A." },
  { id: "child-5a6f", ownerAccountId: "acc-tom-t", displayLabel: "Maya T." },
  { id: "child-2b9e", ownerAccountId: "acc-sam", displayLabel: "Leo P." },
  // Owned by lena (a member who is ALSO a parent) — drives the owner side of
  // Sharing & consent + the "You own this" tag. Demonstrates the role-agnostic model.
  { id: "child-4c7d", ownerAccountId: "acc-lena", displayLabel: "Noah B." },
];

// --- Consents (granular + revocable) -----------------------------------------
// Sharing states from the prototype: Amira/Daniel/Yusuf/Maya = on, Sofia =
// pending (no granted consent yet), Leo = revoked (granted then cut).
export const consents: Consent[] = [
  { id: "consent-amira-lena", scope: ["progress", "results", "export"], grantedBy: "acc-priya-s", grantedAt: "2026-03-12T10:00:00.000Z" },
  { id: "consent-daniel-aisha", scope: ["progress"], grantedBy: "acc-reys", grantedAt: "2026-03-20T10:00:00.000Z" },
  { id: "consent-yusuf-priya", scope: ["progress", "export"], grantedBy: "acc-hana", grantedAt: "2026-04-03T10:00:00.000Z" },
  { id: "consent-maya-aisha", scope: ["progress"], grantedBy: "acc-tom-t", grantedAt: "2026-04-06T10:00:00.000Z" },
  { id: "consent-leo-priya", scope: [], grantedBy: "acc-sam", grantedAt: "2026-04-10T10:00:00.000Z", revokedAt: "2026-06-01T10:00:00.000Z" },
  // Noah (owned by lena) shared with priya — active; david is invited (pending, no grant yet).
  { id: "consent-noah-priya", scope: ["progress", "results"], grantedBy: "acc-lena", grantedAt: "2026-05-02T10:00:00.000Z" },
];

// --- Child links (per-child relationship: owner vs viewer) -------------------

export const childLinks: ChildLink[] = [
  // Owners — full implicit scope, no consent record needed.
  { childId: "child-3f1a", accountId: "acc-priya-s", relation: "owner", scope: ["progress", "results", "export"] },
  { childId: "child-9c2b", accountId: "acc-reys", relation: "owner", scope: ["progress", "results", "export"] },
  { childId: "child-7e44", accountId: "acc-lena-r", relation: "owner", scope: ["progress", "results", "export"] },
  { childId: "child-1d80", accountId: "acc-hana", relation: "owner", scope: ["progress", "results", "export"] },
  { childId: "child-5a6f", accountId: "acc-tom-t", relation: "owner", scope: ["progress", "results", "export"] },
  { childId: "child-2b9e", accountId: "acc-sam", relation: "owner", scope: ["progress", "results", "export"] },
  // lena owns Noah herself (the signed-in account) — full implicit scope.
  { childId: "child-4c7d", accountId: "acc-lena", relation: "owner", scope: ["progress", "results", "export"] },
  // Viewers — org members the owner shared with, holding the consented scope.
  { childId: "child-3f1a", accountId: "acc-lena", relation: "viewer", consentId: "consent-amira-lena", scope: ["progress", "results", "export"], role: "teacher", label: "Ms Lena, class teacher", createdAt: "2026-03-12T09:40:00.000Z" },
  { childId: "child-9c2b", accountId: "acc-aisha", relation: "viewer", consentId: "consent-daniel-aisha", scope: ["progress"], role: "therapist", label: null, createdAt: "2026-03-20T09:00:00.000Z" },
  // Sofia: consent pending — link exists but no granted scope yet.
  { childId: "child-7e44", accountId: "acc-lena", relation: "viewer", scope: [], role: "therapist", label: null, createdAt: "2026-04-01T09:00:00.000Z" },
  { childId: "child-1d80", accountId: "acc-priya", relation: "viewer", consentId: "consent-yusuf-priya", scope: ["progress", "export"], role: "therapist", label: null, createdAt: "2026-04-03T09:00:00.000Z" },
  { childId: "child-5a6f", accountId: "acc-aisha", relation: "viewer", consentId: "consent-maya-aisha", scope: ["progress"], role: "teacher", label: null, createdAt: "2026-04-06T09:00:00.000Z" },
  // Leo: access revoked — scope cut to nothing.
  { childId: "child-2b9e", accountId: "acc-priya", relation: "viewer", consentId: "consent-leo-priya", scope: [], role: "therapist", label: null, createdAt: "2026-04-10T09:00:00.000Z" },
  // Noah (lena's own child) shared with two colleagues: priya active, david pending.
  { childId: "child-4c7d", accountId: "acc-priya", relation: "viewer", consentId: "consent-noah-priya", scope: ["progress", "results"], role: "therapist", label: "Dr Priya, speech", createdAt: "2026-05-02T09:00:00.000Z" },
  { childId: "child-4c7d", accountId: "acc-david", relation: "viewer", scope: [], role: "teacher", label: null, createdAt: "2026-06-02T09:00:00.000Z" },
];

// --- Engagement summaries (aggregated, never scores) -------------------------
// A slice of Amira's recent play (the prototype shows 14 short sessions).
export const sessionSummaries: SessionSummary[] = [
  { childId: "child-3f1a", gameId: "owls-listen-find-letter", durationSeconds: 540, accuracy: 0.9, at: "2026-06-17T09:10:00.000Z" },
  { childId: "child-3f1a", gameId: "turtles-calm-breath", durationSeconds: 360, accuracy: 0.85, at: "2026-06-16T16:30:00.000Z" },
  { childId: "child-3f1a", gameId: "owls-first-sounds", durationSeconds: 660, accuracy: 0.82, at: "2026-06-15T10:05:00.000Z" },
  { childId: "child-9c2b", gameId: "monkeys-count-bananas", durationSeconds: 420, accuracy: 0.6, at: "2026-06-16T11:00:00.000Z" },
  { childId: "child-1d80", gameId: "elephants-3-step-task", durationSeconds: 480, accuracy: 0.75, at: "2026-06-17T08:45:00.000Z" },
  // Noah (lena's own child) — owner can always see full engagement.
  { childId: "child-4c7d", gameId: "monkeys-memory-match", durationSeconds: 300, accuracy: 0.8, at: "2026-06-18T17:20:00.000Z" },
  { childId: "child-4c7d", gameId: "zebras-pattern-line", durationSeconds: 240, accuracy: 0.7, at: "2026-06-17T18:05:00.000Z" },
];

export const skillSummaries: SkillSummary[] = [
  { childId: "child-3f1a", domain: "Listening & letters", level: 0.82, updatedAt: "2026-06-17T09:20:00.000Z" },
  { childId: "child-9c2b", domain: "Counting & numbers", level: 0.6, updatedAt: "2026-06-16T11:10:00.000Z" },
  { childId: "child-7e44", domain: "Calm & feelings", level: 0.48, updatedAt: "2026-06-15T17:00:00.000Z" },
  { childId: "child-1d80", domain: "Memory & steps", level: 0.75, updatedAt: "2026-06-17T08:55:00.000Z" },
  { childId: "child-4c7d", domain: "Memory & steps", level: 0.7, updatedAt: "2026-06-18T17:30:00.000Z" },
];

// --- Progress views (phase 2) — demo data relative to TODAY ------------------
//
// The step states, extra play and centre suggestions below are generated
// relative to the day the module loads, so the demo portal always shows "the
// last 13 weeks" with something in it. Tests of the models pass their own
// inputs and an explicit `now`; nothing asserts on these exact dates.

const TODAY = localDay(new Date());
const THIS_MONDAY = mondayOf(TODAY);
const weeksAgo = (n: number) => addWeeks(THIS_MONDAY, -n);

const STAGE_ORDER: StepStage[] = ["exploring", "help", "own", "steady", "places"];

/** [skillId, step, weeks-ago each stage was reached (explore..places; null = not yet), last practised weeks ago] */
type StepSpec = [string, number, (number | null)[], number];

function stepRows(childId: string, specs: StepSpec[]): SkillStepState[] {
  return specs.map(([skillId, step, reached, last]) => {
    const dates = STAGE_ORDER.map((_, i) => (reached[i] == null ? null : weeksAgo(reached[i] as number)));
    let top = 0;
    dates.forEach((d, i) => {
      if (d) top = i;
    });
    return {
      childId,
      skillId,
      step,
      stage: STAGE_ORDER[top],
      exploreOn: dates[0],
      helpOn: dates[1],
      ownOn: dates[2],
      steadyOn: dates[3],
      placesOn: dates[4],
      lastPractisedOn: weeksAgo(last),
      updatedAt: `${weeksAgo(Math.min(last, 1))}T08:00:00.000Z`,
    };
  });
}

export const stepStates: SkillStepState[] = [
  // Amira — about six months of play across six skills.
  ...stepRows("child-3f1a", [
    ["receptive_language", 1, [26, 24, 20, 16], 2],
    ["receptive_language", 2, [24, 20, 12, 8], 1],
    ["receptive_language", 3, [18, 14, 6], 1],
    ["receptive_language", 4, [10, 5], 0],
    ["vocabulary", 1, [25, 22, 15, 10, 4], 3],
    ["vocabulary", 2, [22, 18, 9], 2],
    ["vocabulary", 3, [12, 7], 2],
    ["vocabulary", 4, [4], 4],
    ["counting_cardinality", 1, [20, 16, 11], 1],
    ["counting_cardinality", 2, [14, 9, 3], 0],
    ["counting_cardinality", 3, [6, 2], 0],
    ["phonological_awareness", 1, [8, 4], 1],
    ["phonological_awareness", 2, [3], 3],
    ["emotional_recognition", 1, [22, 19, 14, 7], 5],
    ["emotional_recognition", 2, [15, 11], 6],
    ["colour_matching", 1, [21, 17, 10, 5, 2], 2],
    ["colour_matching", 2, [13, 8, 1], 1],
  ]),
  // Noah — lena's own child.
  ...stepRows("child-4c7d", [
    ["working_memory", 1, [16, 13, 9, 4], 0],
    ["working_memory", 2, [10, 6, 2], 0],
    ["working_memory", 3, [5, 1], 1],
    ["pattern_recognition", 1, [14, 10, 5], 1],
    ["pattern_recognition", 2, [7, 3], 1],
    ["sorting_matching", 1, [30, 28, 24, 20], 9],
    ["sorting_matching", 2, [28, 25, 18], 9],
    ["sorting_matching", 3, [20, 15], 9],
    ["turn_taking", 1, [6, 2], 2],
  ]),
  ...stepRows("child-9c2b", [
    ["counting_cardinality", 1, [9, 6, 2], 1],
    ["counting_cardinality", 2, [4], 1],
  ]),
  ...stepRows("child-1d80", [
    ["multi_step_instructions", 1, [12, 8, 4], 2],
    ["multi_step_instructions", 2, [6, 3], 2],
  ]),
];

export const supportProfiles: SupportProfile[] = [
  { childId: "child-3f1a", captions: true, reducedMotion: true, highContrast: false, calmMode: true, tapOnly: true, language: "ms", updatedAt: `${weeksAgo(1)}T08:00:00.000Z` },
  { childId: "child-4c7d", captions: true, reducedMotion: false, highContrast: false, calmMode: false, tapOnly: false, language: "en", updatedAt: `${weeksAgo(0)}T08:00:00.000Z` },
  { childId: "child-9c2b", captions: null, reducedMotion: null, highContrast: true, calmMode: null, tapOnly: null, language: "en", updatedAt: `${weeksAgo(2)}T08:00:00.000Z` },
];

/** Recent play for the demo: a few short sittings most weeks, none in some. */
function recentPlay(childId: string, games: string[], pattern: number[][]): SessionSummary[] {
  const out: SessionSummary[] = [];
  pattern.forEach((days, weeksBack) => {
    days.forEach((dayOfWeek, i) => {
      const day = addDays(weeksAgo(weeksBack), dayOfWeek);
      if (day > TODAY) return;
      out.push({
        childId,
        gameId: games[(weeksBack + i) % games.length],
        durationSeconds: 240 + ((weeksBack * 7 + dayOfWeek) % 5) * 60,
        accuracy: null,
        at: new Date(`${day}T17:${String(10 + i * 7).padStart(2, "0")}:00`).toISOString(),
      });
    });
  });
  return out;
}

const recentSessions: SessionSummary[] = [
  ...recentPlay(
    "child-3f1a",
    ["elephant-says-listen", "parrots-word-zoo", "penguins-count-pool", "parrots-rhyme-race", "zebras-color-stripes"],
    [[0, 2], [1, 3, 5], [0], [2, 4], [], [1, 3], [0, 2, 5], [3], [1, 4], [], [2], [0, 3], [1]],
  ),
  ...recentPlay(
    "child-4c7d",
    ["monkeys-memory-match", "zebras-pattern-line", "penguins-turn"],
    [[0, 1], [2], [0, 4], [], [3], [1, 5], [2], [], [0], [4], [], [1], [3]],
  ),
  ...recentPlay("child-9c2b", ["penguins-count-pool"], [[1], [3], [], [2]]),
];
sessionSummaries.push(...recentSessions);

// --- "Seen at home" notes (shared by the family with chosen adults) -----------
// Short, plain observations a parent writes in the app and shares. In the demo
// Lena is the viewer Amira's family shared with, and Noah's own parent.

export const homeNotes: HomeNote[] = [
  { id: "note-amira-1", childId: "child-3f1a", skillId: "counting_cardinality", body: "Counted the stairs up to five on her own, then counted them again going down.", weekOf: weeksAgo(1), createdAt: `${addDays(weeksAgo(1), 3)}T19:00:00.000Z` },
  { id: "note-amira-2", childId: "child-3f1a", skillId: "vocabulary", body: "Named banana and mango at the market and pointed to the next one she wanted.", weekOf: weeksAgo(3), createdAt: `${addDays(weeksAgo(3), 5)}T11:00:00.000Z` },
  { id: "note-amira-old", childId: "child-3f1a", skillId: "receptive_language", body: "Fetched her shoes when asked.", weekOf: weeksAgo(20), createdAt: `${addDays(weeksAgo(20), 1)}T18:00:00.000Z` },
  { id: "note-noah-1", childId: "child-4c7d", skillId: "turn_taking", body: "Waited for his sister's turn in a board game and passed her the dice.", weekOf: weeksAgo(2), createdAt: `${addDays(weeksAgo(2), 6)}T16:30:00.000Z` },
];

// --- Assignments (centre suggestions; "From your centre" in the family's app) --
// Targets are BUILT game ids — the family's app matches a suggestion to a game
// by id. The note is the centre's own words to the family.

const daysAgoIso = (n: number, hour = 9) =>
  new Date(`${addDays(TODAY, -n)}T${String(hour).padStart(2, "0")}:00:00`).toISOString();

export const assignments: Assignment[] = [
  {
    id: "assign-amira",
    childId: "child-3f1a",
    assignedByAccountId: "acc-lena",
    target: "elephant-says-listen",
    status: "in-progress",
    at: daysAgoIso(16),
    note: "We practised in, on and under today. You could try it with toys at bath time.",
    seenAt: daysAgoIso(15, 19),
    updatedAt: daysAgoIso(9, 18),
  },
  {
    id: "assign-amira-2",
    childId: "child-3f1a",
    assignedByAccountId: "acc-lena",
    target: "penguins-count-pool",
    status: "assigned",
    at: daysAgoIso(2),
    note: null,
  },
  { id: "assign-daniel", childId: "child-9c2b", assignedByAccountId: "acc-aisha", target: "penguins-count-pool", status: "assigned", at: daysAgoIso(10) },
  { id: "assign-sofia", childId: "child-7e44", assignedByAccountId: "acc-lena", target: "turtles-calm-breath", status: "explored", at: daysAgoIso(40), seenAt: daysAgoIso(39) },
  {
    id: "assign-noah",
    childId: "child-4c7d",
    assignedByAccountId: "acc-priya",
    target: "zebras-pattern-line",
    status: "explored",
    at: daysAgoIso(12),
    note: "Lovely turn-taking in group today. Patterns are a nice next game.",
    seenAt: daysAgoIso(11, 20),
    updatedAt: daysAgoIso(6, 18),
  },
];

// --- Licence + seats ---------------------------------------------------------

export const license: License = {
  orgId: organisation.id,
  seatsUsed: 5,
  seatsTotal: 12,
  plan: "centre",
  period: "annual",
};

export const seats: Seat[] = [
  { orgId: organisation.id, accountId: "acc-marcus", status: "active" },
  { orgId: organisation.id, accountId: "acc-lena", status: "active" },
  { orgId: organisation.id, accountId: "acc-priya", status: "active" },
  { orgId: organisation.id, accountId: "acc-aisha", status: "active" },
  { orgId: organisation.id, accountId: "acc-david", status: "active" },
  // Tom Reyes — invited, seat pending.
  { orgId: organisation.id, accountId: "acc-tom", status: "pending" },
];
